import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Home, Package, Camera, ClipboardList, Calendar, X, Save, Phone, MapPin, User, Users, AlertTriangle, Key, Info, Sparkles, ChevronDown } from 'lucide-react';
import { Project, Photo } from '../types';
import { getProject, updateProject } from '../services/projectService';
import { getRoomsByProject } from '../services/roomService';
import { getItemsByProject } from '../services/itemService';
import { getPhotosByProject, getTemporaryPhotos, associateTemporaryPhotos } from '../services/photoService';
import { getChecklistsByProject, getChecklistItems } from '../services/checklistService';
import { getDamageReportsByProject } from '../services/damageReportService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

import CalendarSyncStatus from '../components/CalendarSyncStatus';
import { aggregateLensData, formatSEK } from '../utils/lensAggregation';
import CameraComponent from '../components/CameraComponent';
import TemporaryPhotoComponent from '../components/TemporaryPhotoComponent';
import { BottomSheet } from '../components/ui/BottomSheet';
import { PageShell } from '../components/ui/PageShell';
import { StatusBadge, StatusDot, getStatusLabel as uiGetStatusLabel, getStatusDescription } from '../components/ui/StatusBadge';
import { ActionSheet } from '../components/ui/ActionSheet';
import { FloatingActionButton } from '../components/ui/FloatingActionButton';
import { SectionGroup } from '../components/ui/SectionGroup';
import { ActionRow } from '../components/ui/ActionRow';
import { SkeletonList } from '../components/ui/Skeleton';
import { useFullScreen } from '../components/Layout';
import { useNotifications } from '../contexts/NotificationContext';

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { setIsFullScreen } = useFullScreen();
  const { addNotification } = useNotifications();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [calendarSyncNotification, setCalendarSyncNotification] = useState<{
    show: boolean; message: string; type: 'evaluation' | 'pickup' | 'both' | 'error';
  } | null>(null);
  const [projectSaveNotification, setProjectSaveNotification] = useState<{
    show: boolean; message: string; type: string;
  } | null>(null);
  
  const [roomCount, setRoomCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [checklistCount, setChecklistCount] = useState(0);
  const [checklistTotal, setChecklistTotal] = useState(0);
  const [checklistCompleted, setChecklistCompleted] = useState(0);
  const [damageReportCount, setDamageReportCount] = useState(0);
  const [openDamageCount, setOpenDamageCount] = useState(0);
  
  const [projectPhotos, setProjectPhotos] = useState<Photo[]>([]);

  const [showCamera, setShowCamera] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showTempPhotos, setShowTempPhotos] = useState(false);
  const [tempPhotoCount, setTempPhotoCount] = useState(0);
  const [savingPhotos, setSavingPhotos] = useState(false);
  const [photoSaveSuccess, setPhotoSaveSuccess] = useState(false);
  const [showPickupReminder, setShowPickupReminder] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<Project['status'] | null>(null);
  const [showStatusSheet, setShowStatusSheet] = useState(false);

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  useEffect(() => {
    const storedNotification = sessionStorage.getItem('calendarSyncNotification');
    if (storedNotification) {
      try {
        const notification = JSON.parse(storedNotification);
        setCalendarSyncNotification(notification);
        sessionStorage.removeItem('calendarSyncNotification');
      } catch (e) { console.error('Error parsing calendar sync notification:', e); }
    }
    const savedProjectNotification = sessionStorage.getItem('projectSaveNotification');
    if (savedProjectNotification) {
      try {
        const notification = JSON.parse(savedProjectNotification);
        setProjectSaveNotification(notification);
        sessionStorage.removeItem('projectSaveNotification');
        setTimeout(() => setProjectSaveNotification(null), notification.type === 'error' ? 8000 : 3000);
      } catch (e) { console.error('Error parsing project save notification:', e); }
    }
  }, []);
  
  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const fetchedProject = await getProject(projectId);
      if (!fetchedProject) {
        setError('Projektet kunde inte hittas.');
        setLoading(false);
        return;
      }
      setProject(fetchedProject);
      const [rooms, items, photos, checklists, damageReports] = await Promise.all([
        getRoomsByProject(projectId), getItemsByProject(projectId), getPhotosByProject(projectId),
        getChecklistsByProject(projectId), getDamageReportsByProject(projectId)
      ]);
      setRoomCount(rooms.length); setItemCount(items.length); setPhotoCount(photos.length);
      setChecklistCount(checklists.length); setDamageReportCount(damageReports.length);
      setOpenDamageCount(damageReports.filter(r => r.status === 'open').length);
      setProjectPhotos(photos);
      // Fetch checklist items for progress summary
      if (checklists.length > 0) {
        const allChecklistItems = await Promise.all(checklists.map(c => getChecklistItems(c.id)));
        const flat = allChecklistItems.flat();
        setChecklistTotal(flat.length);
        setChecklistCompleted(flat.filter(i => i.completed).length);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching project data:', err);
      setError('Kunde inte hämta projektdata.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  useEffect(() => {
    const tempPhotos = getTemporaryPhotos();
    setTempPhotoCount(tempPhotos.length);
  }, [showCamera, showTempPhotos]);

  const refreshPhotoCount = () => {
    if (!projectId) return;
    getPhotosByProject(projectId)
      .then(photos => setPhotoCount(photos.length))
      .catch(err => console.error('Error refreshing photo count:', err));
  };

  const handleSavePhotosToProject = async () => {
    if (!projectId) return;
    try {
      setSavingPhotos(true);
      await associateTemporaryPhotos(projectId);
      setTempPhotoCount(0);
      setPhotoSaveSuccess(true);
      const photos = await getPhotosByProject(projectId);
      setPhotoCount(photos.length);
      setTimeout(() => setPhotoSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving photos to project:', error);
    } finally {
      setSavingPhotos(false);
    }
  };
  
  const handleStatusChange = async (newStatus: Project['status']) => {
    if (!project || !projectId || updatingStatus) return;
    if (newStatus === 'värderad' && !project.dates.scheduledPickup) {
      setPendingStatus(newStatus);
      setShowPickupReminder(true);
      return;
    }
    try {
      setUpdatingStatus(true);
      const updatedProject = { ...project, status: newStatus, dates: { ...project.dates, lastModified: new Date() } };
      await updateProject(projectId, updatedProject);
      setProject(updatedProject);
    } catch (error) {
      console.error('Error updating project status:', error);
      // The previous version did `setProject({ ...project })` — a no-op
      // reference change that reverted the badge and told the user nothing.
      // A silent revert reads as "the tap didn't register", so the worker taps
      // again against a backend that is still failing.
      if (project) setProject({ ...project });
      addNotification({
        type: 'error',
        message: 'Kunde inte uppdatera status. Kontrollera anslutningen och försök igen.',
        duration: 4000,
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <PageShell title="Laddar..." backTo="/projects" backLabel="Projekt">
        <div className="space-y-4 py-2">
          <SkeletonList count={3} />
          <SkeletonList count={4} />
        </div>
      </PageShell>
    );
  }

  if (error || !project) {
    return (
      <PageShell title="Fel" backTo="/projects" backLabel="Projekt">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          {error || 'Projektet kunde inte hittas.'}
          {!isOnline && <p className="mt-1 text-xs">Du är offline.</p>}
        </div>
      </PageShell>
    );
  }

  // Ordered by lifecycle, labels/descriptions from StatusBadge so this picker
  // can't drift from the badges it sets (it previously had its own wording).
  const statusOptions = (['bostad', 'värderad', 'planerad', 'väntar', 'avbruten', 'avslutad'] as const)
    .map(value => ({ value, label: uiGetStatusLabel(value), description: getStatusDescription(value) }));

  const navItems = [
    { to: `/projects/${projectId}/rooms`, icon: Home, label: 'Rum & Objekt', count: roomCount + itemCount, iconColor: 'text-blue-500' },
    { to: `/projects/${projectId}/photos`, icon: Camera, label: 'Alla foton', count: photoCount, iconColor: 'text-violet-500' },
  ];

  return (
    <PageShell
      title={project.address.street}
      largeTitle
      backTo="/projects"
      backLabel="Projekt"
      headerRight={
        <button
          onClick={() => navigate(`/projects/${projectId}/edit`)}
          className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
        >
          Redigera
        </button>
      }
    >
      {/* Status badge — tappable, below large title */}
      <button
        onClick={() => setShowStatusSheet(true)}
        disabled={updatingStatus}
        className="flex items-center gap-1 mb-3 active:opacity-50 transition-opacity disabled:opacity-50"
      >
        <StatusBadge status={project.status} size="sm" />
        <ChevronDown size={14} className="text-[var(--text-tertiary)]" />
      </button>

      {/* Notifications */}
      {projectSaveNotification?.show && (
        <div className={`px-3.5 py-2.5 rounded-xl flex items-center mb-3 text-sm animate-slide-in-right ${
          projectSaveNotification.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400' : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400'
        }`}>
          <Save size={14} className="mr-2 flex-shrink-0" />
          <p className="flex-1 text-xs font-medium">{projectSaveNotification.message}</p>
          <button onClick={() => setProjectSaveNotification(null)} className="ml-2 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--text-tertiary)]" aria-label="Stäng meddelande"><X size={14} /></button>
        </div>
      )}

      {calendarSyncNotification?.show && (
        <div className={`px-3.5 py-2.5 rounded-xl flex items-center mb-3 text-sm ${
          calendarSyncNotification.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400' : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400'
        }`}>
          <Calendar size={14} className="mr-2 flex-shrink-0" />
          <p className="flex-1 text-xs font-medium">{calendarSyncNotification.message}</p>
          <button onClick={() => setCalendarSyncNotification(null)} className="ml-2 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--text-tertiary)]" aria-label="Stäng meddelande"><X size={14} /></button>
        </div>
      )}

      {/* Pickup Reminder Dialog */}
      <BottomSheet
        isOpen={showPickupReminder}
        onClose={() => { setShowPickupReminder(false); handleStatusChange('planerad'); setPendingStatus(null); }}
        variant="alert"
      >
        <div className="p-5 text-center">
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <Calendar size={20} className="text-amber-600" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Upphämtningsdatum saknas</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Vill du lägga till ett upphämtningsdatum nu?</p>
        </div>
        <div className="flex flex-col gap-2 px-5 pb-5">
          <button
            onClick={() => { setShowPickupReminder(false); navigate(`/projects/${projectId}/edit`); }}
            className="w-full py-3 px-4 text-[15px] font-medium min-h-[44px] rounded-xl bg-accent-600 text-white active:bg-accent-700 transition-colors"
          >
            Lägg till nu
          </button>
          <button
            onClick={() => { setShowPickupReminder(false); handleStatusChange('planerad'); setPendingStatus(null); }}
            className="w-full py-3 px-4 text-[15px] font-medium min-h-[44px] rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] active:bg-[var(--bg-surface-hover)] transition-colors"
          >
            Senare
          </button>
        </div>
      </BottomSheet>

      {/* Status ActionSheet */}
      <ActionSheet
        isOpen={showStatusSheet}
        onClose={() => setShowStatusSheet(false)}
        title="Ändra status"
        actions={statusOptions.map(s => ({
          icon: <StatusDot status={s.value} />,
          label: s.label,
          // Explain what the stage means; mark the current one.
          subtitle: project.status === s.value
            ? `Nuvarande · ${s.description ?? ''}`.trim().replace(/ · $/, '')
            : s.description,
          onClick: () => handleStatusChange(s.value),
          disabled: updatingStatus,
        }))}
      />

      {/* Hero summary card */}
      <SectionGroup className="mb-4">
        <ActionRow
          icon={MapPin}
          iconColor="text-blue-500"
          label={`${project.address.street}, ${project.address.city}`}
          value={project.address.postalCode}
          onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(`${project.address.street}, ${project.address.city}`)}`, '_blank')}
        />
        {project.address.accessCode && (
          <ActionRow
            icon={Key}
            iconColor="text-amber-500"
            label="Portkod"
            value={project.address.accessCode}
            chevron={false}
          />
        )}
        {project.address.accessInfo && (
          <ActionRow
            icon={Info}
            iconColor="text-blue-400"
            label="Övrig info"
            value={project.address.accessInfo}
            chevron={false}
          />
        )}
        {project.contact.phone && (
          <a href={`tel:${project.contact.phone}`}>
            <ActionRow
              icon={Phone}
              iconColor="text-emerald-500"
              label={project.contact.phone}
              value="Ring"
            />
          </a>
        )}
        <ActionRow
          icon={User}
          label={project.contact.name || 'Ingen kontakt'}
          value={project.contact.email}
          chevron={false}
        />
        {project.dates.scheduledPickup && (
          <ActionRow
            icon={Calendar}
            iconColor="text-emerald-500"
            // This row shows the pickup DATE; the status is already on the badge
            // above, so naming it here just repeated the same word twice.
            label="Hämtning"
            value={`${project.dates.scheduledPickup.toDate().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}${project.dates.scheduledPickupTime ? ` kl ${project.dates.scheduledPickupTime}` : ''}`}
            chevron={false}
          />
        )}
        {(() => {
          const lensAgg = aggregateLensData(projectPhotos);
          if (lensAgg.objectCount === 0 && lensAgg.analyzingCount === 0) return null;
          const bestValue = lensAgg.totalMarketValue > 0 ? lensAgg.totalMarketValue : lensAgg.totalEstimatedValue;
          return (
            <ActionRow
              icon={Sparkles}
              iconColor="text-violet-500"
              label={`AI Lens — ${lensAgg.objectCount} objekt${bestValue > 0 ? ` — ${formatSEK(bestValue)}` : ''}`}
              value={lensAgg.analyzingCount > 0 ? `${lensAgg.analyzingCount} analyseras...` : undefined}
              to={`/projects/${projectId}/photos`}
            />
          );
        })()}
      </SectionGroup>

      {/* Navigation */}
      <SectionGroup title="Fältarbete" className="mb-4">
        {navItems.map(item => (
          <ActionRow
            key={item.to}
            icon={item.icon}
            iconColor={item.iconColor}
            label={item.label}
            to={item.to}
            badge={item.count > 0 ? (
              <span className="text-sm text-[var(--text-tertiary)] font-medium tabular-nums">{item.count}</span>
            ) : undefined}
          />
        ))}
      </SectionGroup>

      {/* Inline summaries */}
      {(checklistTotal > 0 || damageReportCount > 0) && (
        <SectionGroup title="Översikt" className="mb-4">
          {checklistTotal > 0 && (
            <ActionRow
              icon={ClipboardList}
              iconColor="text-emerald-500"
              label="Checklist"
              to={`/projects/${projectId}/checklists`}
              value={`${checklistCompleted}/${checklistTotal} klara`}
              badge={
                <div className="w-16 h-1.5 bg-[var(--bg-surface-alt)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${checklistTotal > 0 ? (checklistCompleted / checklistTotal) * 100 : 0}%` }}
                  />
                </div>
              }
            />
          )}
          {damageReportCount > 0 && (
            <ActionRow
              icon={AlertTriangle}
              iconColor="text-red-500"
              label="Skador"
              to={`/projects/${projectId}/damage-reports`}
              value={openDamageCount > 0 ? `${openDamageCount} öppna` : 'Alla lösta'}
              badge={
                <span className="text-sm text-[var(--text-tertiary)] font-medium tabular-nums">{damageReportCount}</span>
              }
            />
          )}
        </SectionGroup>
      )}

      {/* Evaluation details */}
      <SectionGroup title="Hembesök" className="mb-4">
        <ActionRow
          icon={Calendar}
          iconColor="text-violet-500"
          label="Datum"
          value={project.dates.evaluation
            ? `${project.dates.evaluation.toDate().toLocaleDateString('sv-SE')}${project.dates.evaluationStartTime ? ` kl ${project.dates.evaluationStartTime}` : ''}`
            : 'Ej schemalagd'}
          chevron={false}
        />
        <ActionRow
          icon={Users}
          iconColor="text-violet-500"
          label="Värderare"
          value={project.evaluator || '-'}
          chevron={false}
        />
      </SectionGroup>

      {/* Pickup details */}
      <SectionGroup title="Hämtning" className="mb-4">
        <ActionRow
          icon={Calendar}
          iconColor="text-emerald-500"
          label="Planerad"
          value={project.dates.scheduledPickup
            ? `${project.dates.scheduledPickup.toDate().toLocaleDateString('sv-SE')}${project.dates.scheduledPickupTime ? ` kl ${project.dates.scheduledPickupTime}` : ''}`
            : 'Ej schemalagd'}
          chevron={false}
        />
        {project.pickupTeam && project.pickupTeam.length > 0 && (
          <ActionRow
            icon={Users}
            iconColor="text-emerald-500"
            label="Hämtningsteam"
            value={project.pickupTeam.join(', ')}
            chevron={false}
          />
        )}
      </SectionGroup>

      {/* Notes */}
      {project.generalNotes && (
        <SectionGroup title="Anteckningar" className="mb-4">
          <div className="px-4 py-3 text-[15px] text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">
            {project.generalNotes}
          </div>
        </SectionGroup>
      )}

      {/* Calendar Sync */}
      <SectionGroup title="Kalender" className="mb-4">
        <div className="px-4 py-3">
          <CalendarSyncStatus project={project} />
        </div>
      </SectionGroup>

      {/* Spacer so the last section clears the fixed bottom tab bar
          (49px tab bar + iOS safe-area inset). Without this the Kalender /
          Hämtning sections render behind the tab bar on mobile. */}
      <div className="lg:hidden" style={{ height: 'calc(49px + env(safe-area-inset-bottom, 0px) + 16px)' }} aria-hidden="true" />

      {/* Camera FAB */}
      {photoSaveSuccess && (
        <div className="fixed right-4 z-20 lg:bottom-[5.5rem] animate-slide-up" style={{ bottom: 'calc(49px + env(safe-area-inset-bottom, 0px) + 80px)' }}>
          <div className="bg-emerald-100 text-emerald-800 px-3 py-2 rounded-xl shadow-sm text-xs font-medium">
            Foton sparade
          </div>
        </div>
      )}

      {/* One tap to the viewfinder. The sheet only appears when temporary photos
          are waiting, since that is the only time there is anything else to choose. */}
      <FloatingActionButton
        onClick={() => (tempPhotoCount > 0 ? setShowFabMenu(true) : setShowCamera(true))}
        badge={tempPhotoCount}
        aria-label={tempPhotoCount > 0 ? 'Fotoåtgärder' : 'Ta foto'}
      />

      <ActionSheet
        isOpen={showFabMenu}
        onClose={() => setShowFabMenu(false)}
        actions={[
          {
            icon: <Camera size={20} className="text-accent-600" />,
            label: 'Ta foto',
            onClick: () => setShowCamera(true),
          },
          ...(tempPhotoCount > 0 ? [
            {
              icon: <Package size={20} className="text-amber-500" />,
              label: `Visa tillfälliga foton (${tempPhotoCount})`,
              onClick: () => setShowTempPhotos(true),
            },
            {
              icon: <Save size={20} className="text-emerald-500" />,
              label: 'Spara foton till projektet',
              subtitle: savingPhotos ? 'Sparar...' : undefined,
              onClick: handleSavePhotosToProject,
              disabled: savingPhotos,
            },
          ] : []),
        ]}
      />
      
      {showCamera && (
        <CameraComponent
          projectId={projectId}
          onPhotoTaken={() => refreshPhotoCount()}
          onClose={() => {
            setShowCamera(false);
            refreshPhotoCount();
          }}
        />
      )}
      
      {showTempPhotos && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-surface)] rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <TemporaryPhotoComponent onClose={() => setShowTempPhotos(false)} />
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ProjectDetailPage;
